[
<template>
  <teleport to="body">
    <transition
      name="fade"
      appear>
      <div
        v-if="showModal"
        class="modal-bg"
        @click="closeModal"></div>
    </transition>
  </teleport>

  <teleport to="body">
    <transition
      name="fade"
      appear>
      <div
        v-if="showModal"
        class="modal">
        <div class="modal-content">
          <div class="video-container z-[200]">
            <div
              class="video-frame"
              :class="{
                iframe: isEmbedded,
              }">
              <iframe
                v-if="isEmbedded && embedUrl"
                :src="embedUrl"
                frameborder="0"
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowfullscreen></iframe>
              <video
                v-else
                :src="urlValue"
                controls
                autoplay
                loop
                playsinline></video>
            </div>
            <div
              class="close"
              @click="closeModal">
              <span class="down first"></span>
              <span class="down second"></span>
              <span class="up first"></span>
              <span class="up second"></span>
            </div>

            <div
              v-if="isEmbedded"
              class="opacity-0 pointer-events-none">
              {{ embedUrl }}
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { computed, watch } from 'vue'
import locoScroll from '@/assets/js/lenisScroll'

import { useModal } from '@/composables/useModal.js'

const { showModal, closeModal, urlValue, isEmbedded } = useModal()

watch(showModal, (value) => {
  value ? locoScroll.stop() : locoScroll.start()
})

const embedUrl = computed(() => {
  const rawUrl = urlValue.value?.trim()
  if (!rawUrl) return null

  const patterns = [/vimeo\.com\/(\d+)/, /player\.vimeo\.com\/video\/(\d+)/]

  let videoId = null
  for (const pattern of patterns) {
    const match = rawUrl.match(pattern)
    if (match?.[1]) {
      videoId = match[1]
      break
    }
  }

  if (!videoId) return null

  return `https://player.vimeo.com/video/${videoId}?autoplay=1&loop=0&muted=0&autopause=0&controls=1&keyboard=1&title=0&byline=0&portrait=0`
})
</script>

<style>
@reference '@/assets/styles/main.css';
.modal-bg {
  @apply fixed top-0 bg-black/60 left-0 w-screen p-xs md:p-5 h-screen flex justify-center backdrop-blur-[2px] items-center overflow-hidden z-[100];
}

.modal {
  @apply fixed top-0 left-0 w-screen p-xs md:p-5 h-screen flex justify-center items-center overflow-hidden z-[200];

  .modal-content {
    @apply w-full p-1 min-h-[60%] max-h-[90vh] flex flex-row z-10 cursor-pointer justify-center items-stretch lg:min-h-[469px] lg:h-fit lg:w-fit;

    .open-externally {
      @apply h-auto flex;

      .text-container {
        @apply relative p-10 w-full flex flex-col max-w-[937px] justify-center lg:w-[937px] mx-auto overflow-hidden bg-white;

        .text {
          @apply w-full lg:w-[590px] mx-auto flex flex-col;

          h2 {
            @apply header-5 mb-4;
          }
        }
      }
    }

    .video-container {
      @apply relative mx-auto w-full max-w-[1115px];
      max-height: 90vh;

      .video-frame {
        position: relative;
        width: 100%;
        overflow: hidden;

        &.iframe {
          padding: 0 0 56.25%;
          height: 0;
        }

        iframe {
          @apply absolute w-full h-full;
        }

        video {
          @apply relative w-full h-full object-cover max-w-[900px];
        }
      }

      img.video-icon {
        @apply transition-opacity duration-300 delay-100 absolute top-1/2 left-1/2 
      w-16 h-16 -translate-x-1/2 -translate-y-1/2 z-4 cursor-pointer 
      md:w-[150px] md:h-[150px];
      }

      &.playing img.video-icon {
        @apply opacity-0 pointer-events-none;
      }

      .close {
        @apply absolute -top-16 right-0 w-10 h-10 flex justify-center;

        span {
          @apply bg-white w-10 transform transition-transform duration-300 ease-in-out;

          &.down {
            @apply absolute;
            right: -3px;
            top: 23px;
            transform: rotate(45deg);

            &.first {
              top: 20px;
              right: -6px;
            }
          }

          &.up {
            @apply absolute;
            right: -3px;
            top: 21px;
            transform: rotate(-45deg);

            &.first {
              top: 23px;
              right: -7px;
            }
          }
        }
      }
    }

    .close {
      @apply flex flex-col justify-center items-center bg-transparent transition-all cursor-pointer rounded-full absolute right-2 w-[50px] h-[50px] top-2 pointer-events-auto z-30;

      span {
        @apply h-[1px] w-[30px] bg-trace-purple-400 block;
      }
    }
  }
}
</style>
]
